import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { headings, numberedHeadings, readDocument } from "@/office/word/document";

export const lektionUeberschriftenNummerieren: Lesson = {
  id: "ueberschriften-nummerieren",
  title: "Überschriften automatisch nummerieren",
  summary: "Aus 1., 1.1, 1.1.1 wird eine Nummerierung, die sich selbst pflegt.",
  durationMinutes: 9,
  learningGoals: [
    "Eine Liste mit mehreren Ebenen mit den Überschriftenvorlagen verknüpfen",
    "Den Unterschied zu getippten Nummern erkennen",
    "Die Nummerierung im ganzen Dokument einheitlich halten",
  ],
  steps: [
    {
      id: "liste-verknuepfen",
      title: "Die Nummerierung verknüpfen",
      explanation: [
        "In Berichten, Handbüchern und wissenschaftlichen Arbeiten sind Kapitel " +
          "nummeriert: 1., 1.1, 1.1.1. Getippt sind diese Nummern eine Zeitbombe – " +
          "sobald ein Kapitel dazukommt, stimmt alles darunter nicht mehr, und im " +
          "Inhaltsverzeichnis erst recht nicht.",
        "Word kann das übernehmen, und zwar über die Verknüpfung einer Liste mit " +
          "mehreren Ebenen mit den Überschriftenvorlagen. Der Weg: Cursor in eine " +
          "Überschrift 1, dann auf der Registerkarte „Start“ die Schaltfläche „Liste mit " +
          "mehreren Ebenen“ und in der Auswahl eine Variante aus dem Bereich " +
          "„Listenbibliothek“, die „Überschrift 1, Überschrift 2 …“ in der Vorschau zeigt.",
        "Danach nummeriert Word jede Überschrift automatisch – und richtet die " +
          "Nummerierung an der Ebene aus. Eine Überschrift 2 bekommt 1.1, eine " +
          "Überschrift 3 wird zu 1.1.1. Damit zahlt sich die saubere Ebenenstruktur aus " +
          "dem Formatvorlagen-Modul ein weiteres Mal aus.",
      ],
      keyPoints: [
        "Start → Liste mit mehreren Ebenen → Variante mit „Überschrift 1, Überschrift 2 …“.",
        "Die Nummer entsteht aus der Überschriftenebene, nicht aus dem Text.",
        "Getippte Nummern verrutschen beim ersten eingefügten Kapitel.",
      ],
      task: {
        instruction:
          "Baue eine Gliederung mit mindestens einer Überschrift 1 und zwei Überschriften 2 " +
          "und verknüpfe sie mit einer nummerierten Liste mit mehreren Ebenen.",
        hint: "Cursor in die erste Überschrift setzen, dann Start → Liste mit mehreren Ebenen. Die richtige Variante erkennst du an „Überschrift“ in der Vorschau.",
        check: async () => {
          const snapshot = await readDocument();
          const alle = headings(snapshot);
          if (alle.length === 0) {
            return fail(
              "Im Dokument gibt es noch keine Überschriften.",
              "Weise zuerst die Vorlagen „Überschrift 1“ und „Überschrift 2“ zu.",
            );
          }

          const nummeriert = numberedHeadings(snapshot);
          const ebene2 = alle.filter((eintrag) => eintrag.level === 2);

          return fromCriteria(
            [
              criterion(`Mindestens drei Überschriften (gefunden: ${alle.length})`, alle.length >= 3),
              criterion(`Mindestens zwei Überschriften der Ebene 2 (gefunden: ${ebene2.length})`, ebene2.length >= 2),
              criterion(
                `Die Überschriften sind automatisch nummeriert (gefunden: ${nummeriert.length})`,
                nummeriert.length >= 3,
              ),
            ],
            {
              success: `Die Nummerierung läuft – die erste Überschrift trägt jetzt „${
                nummeriert[0]?.list?.listString ?? ""
              }“.`,
              failure: "Die Nummerierung sitzt noch nicht.",
              hint: "Eine getippte Zahl am Zeilenanfang zählt nicht. Word muss die Nummer selbst erzeugen – dann lässt sie sich auch nicht anklicken oder löschen.",
            },
          );
        },
      },
    },
    {
      id: "tuecken",
      title: "Die Tücken der Nummerierung",
      explanation: [
        "Nummerierungen in Word haben den Ruf, störrisch zu sein. Das liegt fast immer an " +
          "zwei Dingen.",
        "Erstens: Es gibt einen Unterschied zwischen der Listenbibliothek (allgemeine " +
          "Vorlagen) und „Aktuelle Liste“ (die im Dokument bereits verwendete). Wählst du " +
          "aus Versehen zweimal eine Variante aus der Bibliothek, entstehen zwei " +
          "voneinander unabhängige Listen – und die zweite beginnt wieder bei 1.",
        "Zweitens: Über den Rechtsklick auf eine nummerierte Überschrift erreichst du " +
          "„Neu beginnen mit 1“ und „Nummerierung fortsetzen“. Wenn ein Kapitel " +
          "unerwartet wieder bei 1 anfängt, ist hier der Hebel.",
        "Vermeiden lässt sich das meiste, indem du die Nummerierung genau einmal am " +
          "Anfang zuweist und danach ausschließlich über die Überschriftenvorlagen " +
          "arbeitest. Neue Kapitel bekommen dann automatisch die richtige Nummer.",
      ],
      keyPoints: [
        "„Aktuelle Liste“ statt erneut aus der Listenbibliothek wählen.",
        "Rechtsklick → „Nummerierung fortsetzen“ repariert eine unterbrochene Zählung.",
        "Einmal zuweisen, danach nur noch Überschriftenvorlagen verwenden.",
      ],
    },
  ],
};
